import { API_URL as BASE_URL } from '~/core/constants';
import { Address, Hex } from "viem";
import { getHeaders, getOrThrow } from "~/core/api";
import { apiAuthTokenStorage } from "~/core/storages";
import { BLINKO_GAME_CONFIG_CID_V2, BLINKO_HARD_MODE_CONFIG_CID_V1, BLINKO_HARD_MODE_CONFIG_CID_V2 } from './game-engine/shared-constants';
import { BlinkoVersion } from './game-engine';
import { SHARE_WORKER_URL } from './constants';

export type BlinkoToken = 'ETH' | 'USDC';
export type BlinkoDifficulty = 'easy' | 'medium' | 'hard';
export type BlinkoGameStatus = 'active' | 'won' | 'lost';
export type BlinkoSignatureType = 'cashOut' | 'markAsLost';

export interface BlinkoGameParams {
  token: string;
  betAmount: string;
  gameSeedHash: string;
  algorithm: string;
  gameConfig: string;
  resolver: string;
  deadline: string;
}

export interface BlinkoGameResult {
  bins: number[];
  totalWin: string;
  rtp: number;
}

export interface BlinkoProfileStats {
  totalGames: number;
  totalWon: number;
  totalLost: number;
  totalBet: string;
  totalPayout: string;
  referralsCount?: number;
  totalGamesPlayed?: number;
  highestMultiplier?: number;
  bonusGamesReached?: number;
  highestBonusLevel?: number;
  currentDailyStreak?: number;
  maxDailyStreak?: number;
  todayHoney?: string;
  todayReferralHoney?: string;
  todayTotalHoney?: string;
  dailyRankings?: Record<string, number>;
}

export interface BlinkoProfile {
  address: Address;
  name: string | null;
  picture: string | null;
  twitterId?: string | null;
  twitterUsername?: string | null;
  twitterHandle?: string | null;
  harvestedHoney: string;
  referralHoney: string;
  totalHoney: string;
  profitShareBps: number | null;
  profitShareUntil: number | null;
  profitShareEarnings: string;
  settledProfitShare: string;
  pendingProfitShare: string;
  referralCode: string | null;
  referredByAddress: Address | null;
  usedReferralCode: string | null;
  restrictedUntil: number | null;
  createdAt: number;
  stats?: BlinkoProfileStats;
}

export interface BlinkoGame {
  gameId: string;
  player: Address;
  token: string;
  betAmount: string;
  pinRows: number;
  difficulty: BlinkoDifficulty;
  numDrops: number;
  gameSeed: string;
  gameSeedHash: string;
  algorithm: string;
  gameConfig: string;
  resolver: string;
  salt: string | null;
  status: BlinkoGameStatus;
  payoutAmount: string | null;
  gameState: BlinkoGameResult | null;
  gameStateHash: string | null;
  createGameSignature: string;
  cashOutSignature: string | null;
  markAsLostSignature: string | null;
  deadline: string;
  createdAt: string;
  updatedAt: string;
}

export interface BlinkoPlayerStats {
  totalGames: number;
  totalWon: number;
  totalLost: number;
  totalBet: string;
  totalPayout: string;
}

export interface BlinkoHoneyBalance {
  harvested: string;
  referral: string;
  total: string;
}

export interface CreateGameRequest {
  player: Address;
  token: BlinkoToken;
  betAmount: string;
  hardMode?: boolean;
  v2Mode?: boolean;
}

export interface CreateGameRequestWithToken extends CreateGameRequest {
  selectedToken: BlinkoToken;
}

export interface CreateGameResponse {
  signature: Hex;
  gameSeedHash: string;
  deadline: string;
  gameId: string;
  gameParams: BlinkoGameParams;
}

export interface PlayGameResponse {
  result: BlinkoGameResult;
  signature: Hex;
  signatureType: BlinkoSignatureType;
  gameState: string;
  gameSeed: string;
  deadline: string;
  salt: string;
}

export interface BlinkoPaginatedGamesResponse {
  games: BlinkoGame[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
    hasMore: boolean;
  };
}

export interface BlinkoLoginResponse {
  token: string;
}

export interface BlinkoReferralCodeResponse {
  referralCode: string;
}

export interface BlinkoReferralProfile {
  address: Address;
  name: string | null;
  createdAt: number;
}

export interface BlinkoReferrerProfile {
  address: Address;
  name: string | null;
  twitterHandle: string | null;
}

export interface BlinkoReferralStats {
  referralCode: string;
  directReferrals: number;
  totalReferralHoney: string;
}

export interface BlinkoLeaderboardEntry {
  rank: number;
  address: Address;
  name: string | null;
  picture: string | null;
  twitterHandle: string | null;
  harvestedHoney: string;
  referralHoney: string;
  totalHoney: string;
}

export interface BlinkoDailyLeaderboardEntry {
  rank: number;
  address: Address;
  name: string | null;
  picture: string | null;
  twitterHandle: string | null;
  dailyHoney: string;
}

export interface BlinkoLeaderboardV2Response {
  leaderboard: BlinkoLeaderboardEntry[];
  total: number;
  myRank: BlinkoLeaderboardEntry | null;
}

export interface BlinkoDailyLeaderboardV2Response {
  leaderboard: BlinkoDailyLeaderboardEntry[];
  date: string;
  total: number;
  myRank: BlinkoDailyLeaderboardEntry | null;
}

export interface BlinkoClanLeaderboardEntry {
  rank: number;
  address: Address;
  name: string | null;
  picture: string | null;
  twitterHandle: string | null;
  harvestedHoney: string;
  referralHoney: string;
  totalHoney: string;
}

export type BlinkoClanLeaderboardResponse = BlinkoClanLeaderboardEntry[];

export interface BlinkoSinceResetLeaderboardEntry {
  rank: number;
  address: Address;
  name: string | null;
  picture: string | null;
  twitterHandle: string | null;
  harvestedHoney: string;
  baselineAt: number | null;
}

export interface BlinkoSinceResetLeaderboardResponse {
  since: number | null;
  leaderboard: BlinkoSinceResetLeaderboardEntry[];
}

export interface BlinkoLeaderboardResetResponse {
  resetAt: number;
}

const isTokenValid = (token: string, buffer: number = 0): boolean => {
  const tokenData = JSON.parse(atob(token.split('.')[1]));
  const expirationTime = tokenData.exp * 1000; // Convert to milliseconds
  return Date.now() < (expirationTime - buffer);
};

export interface BlinkoTwitterInitResponse {
  url: string;
  oauthToken: string;
}

export interface BlinkoTwitterAuthorizeResponse {
  twitterId: string;
  twitterUsername: string;
  twitterHandle: string;
  profileImageUrl: string;
}

export const checkVersionMode = (game: BlinkoGame): { version: BlinkoVersion; isHardMode: boolean } => {
  const config = game.gameConfig.toLowerCase();
  const isV2 = config === BLINKO_GAME_CONFIG_CID_V2.toLowerCase() || 
               config === BLINKO_HARD_MODE_CONFIG_CID_V2.toLowerCase();
  const isHardMode = config === BLINKO_HARD_MODE_CONFIG_CID_V1.toLowerCase() || 
                     config === BLINKO_HARD_MODE_CONFIG_CID_V2.toLowerCase();
  return {
    version: isV2 ? 'v2' : 'v1',
    isHardMode,
  }
};

export const api = {
  /**
   * Authenticates a player with the Blinko backend and stores the JWT token
   */
  login: async (address: Address, signature: string): Promise<string> => {
    const response = await fetch(`${BASE_URL}/blinko/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ address, signature }),
    });

    const { data } = await getOrThrow(response, 'Failed to login to Blinko');
    const token = (data as BlinkoLoginResponse).token;
    if (token) {
      apiAuthTokenStorage.set(address, token);
    }
    return token;
  },

  /**
   * Creates a new Blinko game and returns the signature for on-chain creation
   */
  createGame: async (address: Address, request: CreateGameRequest): Promise<CreateGameResponse> => {
    const response = await fetch(`${BASE_URL}/blinko/create-game`, {
      method: 'POST',
      headers: getHeaders(address),
      body: JSON.stringify(request),
    });
    
    const { data } = await getOrThrow(response, 'Failed to create Blinko game');
    return data;
  },

  /**
   * Plays a game after the salt has been committed on-chain
   */
  playGame: async (address: Address, gameId: string, txHash?: string): Promise<PlayGameResponse> => {
    const response = await fetch(`${BASE_URL}/blinko/games/${gameId}/play`, {
      method: 'POST',
      headers: getHeaders(address),
      body: JSON.stringify({ txHash }),
    });

    const { data } = await getOrThrow(response, 'Failed to play Blinko game');
    return data;
  },

  /**
   * Retrieves game information by game ID
   */
  getGame: async (gameId: string): Promise<BlinkoGame> => {
    const response = await fetch(`${BASE_URL}/blinko/games/${gameId}`, {
      method: 'GET',
      headers: getHeaders(),
    });

    const { data } = await getOrThrow(response, 'Failed to get Blinko game');
    return data;
  },

  /**
   * Retrieves games for a specific player
   */
  getPlayerGames: async (address: Address, limit: number = 20, offset: number = 0): Promise<BlinkoPaginatedGamesResponse> => {
    const response = await fetch(`${BASE_URL}/blinko/players/${address}/games?limit=${limit}&offset=${offset}`, {
      method: 'GET',
      headers: getHeaders(address),
    });

    const { data, pagination } = await getOrThrow(response, 'Failed to get player games');
    return { games: data, pagination } as BlinkoPaginatedGamesResponse;
  },

  /**
   * Retrieves aggregated statistics for a player
   */
  getPlayerStats: async (address: Address): Promise<BlinkoPlayerStats> => {
    const response = await fetch(`${BASE_URL}/blinko/players/${address}/stats`, {
      method: 'GET',
      headers: getHeaders(address),
    });

    const { data } = await getOrThrow(response, 'Failed to get player stats');
    return data;
  },

  /**
   * Retrieves a player's profile
   */
  getProfile: async (address: Address): Promise<BlinkoProfile> => {
    const response = await fetch(`${BASE_URL}/blinko/profiles/${address}`, {
      method: 'GET',
      headers: getHeaders(address),
    });

    const { data } = await getOrThrow(response, 'Failed to get Blinko profile');
    return data;
  },

  /**
   * Updates a player's profile
   */
  updateProfile: async (address: Address, payload: { name?: string; picture?: string }): Promise<BlinkoProfile> => {
    const response = await fetch(`${BASE_URL}/blinko/profiles/${address}`, {
      method: 'PUT',
      headers: getHeaders(address),
      body: JSON.stringify(payload),
    });

    const { data } = await getOrThrow(response, 'Failed to update Blinko profile');
    return data;
  },

  /**
   * Retrieves a player's honey balance
   */
  getHoneyBalance: async (address: Address): Promise<BlinkoHoneyBalance> => {
    const response = await fetch(`${BASE_URL}/blinko/profiles/${address}/honey`, {
      method: 'GET',
      headers: getHeaders(address),
    });

    const { data } = await getOrThrow(response, 'Failed to get honey balance');
    return data;
  },

  /**
   * Sets self-exclusion restriction
   */
  setRestriction: async (address: Address, restrictedUntil: number): Promise<{ address: Address; restrictedUntil: number }> => {
    const response = await fetch(`${BASE_URL}/blinko/profiles/${address}/restriction`, {
      method: 'POST',
      headers: getHeaders(address),
      body: JSON.stringify({ restrictedUntil: Math.floor(Date.now() / 1000) + restrictedUntil }),
    });

    const { data } = await getOrThrow(response, 'Failed to set restriction');
    return data;
  },

  /**
   * Removes self-exclusion restriction
   */
  removeRestriction: async (address: Address): Promise<{ address: Address; restrictedUntil: null }> => {
    const response = await fetch(`${BASE_URL}/blinko/profiles/${address}/restriction`, {
      method: 'DELETE',
      headers: getHeaders(address),
    });

    const { data } = await getOrThrow(response, 'Failed to remove restriction');
    return data;
  },

  /**
   * Retrieves or updates referral code
   */
  getReferralCode: async (address: Address): Promise<BlinkoReferralCodeResponse> => {
    const response = await fetch(`${BASE_URL}/blinko/referral/${address}/code`, {
      method: 'GET',
      headers: getHeaders(address),
    });

    const { data } = await getOrThrow(response, 'Failed to get referral code');
    return data;
  },

  updateReferralCode: async (address: Address, referralCode: string): Promise<BlinkoReferralCodeResponse> => {
    const response = await fetch(`${BASE_URL}/blinko/referral/${address}/code`, {
      method: 'PUT',
      headers: getHeaders(address),
      body: JSON.stringify({ referralCode }),
    });

    const { data } = await getOrThrow(response, 'Failed to update referral code');
    return data;
  },

  applyReferralCode: async (address: Address, referralCode: string): Promise<void> => {
    const response = await fetch(`${BASE_URL}/blinko/referral/${address}/apply`, {
      method: 'POST',
      headers: getHeaders(address),
      body: JSON.stringify({ referralCode }),
    });

    await getOrThrow(response, 'Failed to apply referral code');
  },

  getReferrer: async (address: Address): Promise<BlinkoReferrerProfile | null> => {
    const response = await fetch(`${BASE_URL}/blinko/referral/${address}/referrer`, {
      method: 'GET',
      headers: getHeaders(address),
    });

    const { data } = await getOrThrow(response, 'Failed to get referrer');
    return data;
  },

  getReferrals: async (address: Address): Promise<BlinkoReferralProfile[]> => {
    const response = await fetch(`${BASE_URL}/blinko/referral/${address}/referrals`, {
      method: 'GET',
      headers: getHeaders(address),
    });

    const { data } = await getOrThrow(response, 'Failed to get referrals');
    return data;
  },

  getReferralStats: async (address: Address): Promise<BlinkoReferralStats> => {
    const response = await fetch(`${BASE_URL}/blinko/referral/${address}/stats`, {
      method: 'GET',
      headers: getHeaders(address),
    });

    const { data } = await getOrThrow(response, 'Failed to get referral stats');
    return data;
  },

  /**
   * Leaderboard endpoints
   */
  getLeaderboard: async (address?: string): Promise<BlinkoLeaderboardV2Response> => {
    const url = new URL(`${BASE_URL}/blinko/v2/leaderboard`);
    if (address) url.searchParams.append('address', address);

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: getHeaders(),
    });

    const { data } = await getOrThrow(response, 'Failed to get leaderboard');
    return data;
  },

  /**
   * Retrieves a clan (referral-code-scoped) leaderboard
   */
  getClanLeaderboard: async (code: string): Promise<BlinkoClanLeaderboardResponse> => {
    const response = await fetch(`${BASE_URL}/blinko/referral/code/${code}/leaderboard`, {
      method: 'GET',
      headers: getHeaders(),
    });

    const { data } = await getOrThrow(response, 'Failed to get clan leaderboard');
    return data;
  },

  /**
   * Retrieves the since-reset clan leaderboard (harvestedHoney delta since last reset)
   */
  getClanLeaderboardSinceReset: async (code: string): Promise<BlinkoSinceResetLeaderboardResponse> => {
    const response = await fetch(`${BASE_URL}/blinko/referral/code/${code}/leaderboard/since-reset`, {
      method: 'GET',
      headers: getHeaders(),
    });

    const { data } = await getOrThrow(response, 'Failed to get clan leaderboard');
    return data;
  },

  /**
   * Snapshots referral baselines and resets the since-reset leaderboard for the referrer
   */
  resetReferralLeaderboard: async (address: Address): Promise<BlinkoLeaderboardResetResponse> => {
    const response = await fetch(`${BASE_URL}/blinko/referral/${address}/leaderboard/reset`, {
      method: 'POST',
      headers: getHeaders(address),
    });

    const { data } = await getOrThrow(response, 'Failed to reset leaderboard');
    return data;
  },

  getDailyLeaderboard: async (address?: string): Promise<BlinkoDailyLeaderboardV2Response> => {
    const url = new URL(`${BASE_URL}/blinko/v2/leaderboard/daily`);
    if (address) url.searchParams.append('address', address);

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: getHeaders(),
    });

    const { data } = await getOrThrow(response, 'Failed to get daily leaderboard');
    return data;
  },

  /**
   * Twitter auth endpoints for Blinko
   */
  initTwitterAuth: async (callbackUrl: string): Promise<BlinkoTwitterInitResponse> => {
    const response = await fetch(`${BASE_URL}/blinko/auth/twitter/init`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({ callbackUrl }),
    });

    const { data } = await getOrThrow(response, 'Failed to initialize Twitter auth');
    return data;
  },

  authorizeTwitter: async (address: Address, oauthToken: string, oauthVerifier: string): Promise<BlinkoTwitterAuthorizeResponse> => {
    const response = await fetch(`${BASE_URL}/blinko/auth/twitter/authorize`, {
      method: 'POST',
      headers: getHeaders(address),
      body: JSON.stringify({ oauthToken, oauthVerifier }),
    });

    const { data } = await getOrThrow(response, 'Failed to authorize Twitter');
    return data;
  },

  disconnectTwitter: async (address: Address): Promise<void> => {
    const response = await fetch(`${BASE_URL}/blinko/auth/twitter/disconnect`, {
      method: 'POST',
      headers: getHeaders(address),
    });

    await getOrThrow(response, 'Failed to disconnect Twitter');
  },

  getAnnouncement: async () => {
    const response = await fetch(`https://announcement-worker.blinko.workers.dev/blinko`, {
      method: 'GET',
    });
    const data = await getOrThrow(response, '');
    return data
  },

  createShareLink: async (
    address: Address | undefined,
    payload: { winAmount: number; multiplier: number; refCode: string; gameId: string }
  ): Promise<{ url: string; shareId: string }> => {
    const response = await fetch(`${SHARE_WORKER_URL}/api/share`, {
      method: 'POST',
      headers: getHeaders(address),
      body: JSON.stringify(payload),
    });
    const data = await getOrThrow(response, 'Failed to generate share link');
    return data
  },
};

export const isBlinkoLoginTokenValid = (address: Address, buffer: number = 0): boolean => {
  const token = apiAuthTokenStorage.get(address);
  return Boolean(token) && isTokenValid(token!, buffer);
};
